/**
 * Maps Azure Document Intelligence `prebuilt-idDocument` (driver license) analyze result
 * into Patient AI intake fields: gender, date of birth, mobile, and address when present.
 */

import {
  formatUsAddressDisplay,
  parseUsMailingAddress,
  type ParsedUsAddress,
} from '../utils/usAddress'

export type { ParsedUsAddress }

export type IntakeFormPatchFromDrivingLicence = {
  patientDOB: string
  patientGender: string
  patientPhone: string
  patientFirstName: string
  patientLastName: string
  patientAddress: string
  patientCity: string
  patientState: string
  patientZip: string
}

const EMPTY: IntakeFormPatchFromDrivingLicence = {
  patientDOB: '',
  patientGender: '',
  patientPhone: '',
  patientFirstName: '',
  patientLastName: '',
  patientAddress: '',
  patientCity: '',
  patientState: '',
  patientZip: '',
}

function unwrapNestedPayload(payload: unknown, depth = 0): unknown {
  if (depth > 5) return payload
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload
  const record = payload as Record<string, unknown>
  if (record.analyzeResult && typeof record.analyzeResult === 'object') return payload
  if (Array.isArray(record.documents)) return payload
  for (const key of ['data', 'result', 'body', 'payload']) {
    if (key in record && record[key] != null) {
      return unwrapNestedPayload(record[key], depth + 1)
    }
  }
  return payload
}

function fieldAsString(field: unknown): string {
  if (field === null || field === undefined) return ''
  if (typeof field === 'string') return field.trim()
  if (typeof field !== 'object') return String(field).trim()
  const o = field as Record<string, unknown>
  if (typeof o.valueString === 'string') return o.valueString.trim()
  if (typeof o.valueDate === 'string') return o.valueDate.trim()
  if (typeof o.valuePhoneNumber === 'string') return o.valuePhoneNumber.trim()
  if (typeof o.valueNumber === 'number' && Number.isFinite(o.valueNumber)) {
    return String(o.valueNumber)
  }
  if (typeof o.value === 'string') return o.value.trim()
  if (typeof o.content === 'string') return o.content.trim()
  return ''
}

function normalizeDateForInput(raw: string): string {
  const t = raw.trim()
  if (!t) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10)
  const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
  if (m) {
    const mm = m[1].padStart(2, '0')
    const dd = m[2].padStart(2, '0')
    const yy = m[3]
    return `${yy}-${mm}-${dd}`
  }
  return t
}

function normalizeGender(g: string): string {
  const u = g.trim().toUpperCase()
  if (u === 'M' || u === 'MALE') return 'male'
  if (u === 'F' || u === 'FEMALE') return 'female'
  if (u === 'X' || u === 'OTHER' || u === 'O') return 'other'
  const lower = g.trim().toLowerCase()
  if (lower === 'male' || lower === 'female' || lower === 'other') return lower
  return ''
}

function normalizePhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '').trim()
  return digits
}

function getAnalyzeResult(payload: unknown): Record<string, unknown> | null {
  const root = unwrapNestedPayload(payload)
  if (!root || typeof root !== 'object' || Array.isArray(root)) return null
  const record = root as Record<string, unknown>
  const ar = record.analyzeResult
  if (ar && typeof ar === 'object' && !Array.isArray(ar)) {
    return ar as Record<string, unknown>
  }
  // Some gateways may already return the analyzeResult object at the top level.
  if ('documents' in record || 'content' in record) return record
  return null
}

function getDocumentFields(payload: unknown): Record<string, unknown> | null {
  const analyzeResult = getAnalyzeResult(payload)
  if (!analyzeResult) return null
  const documents = analyzeResult.documents
  if (!Array.isArray(documents) || documents.length === 0) return null
  // Prefer a driver-license document when multiple docs are returned.
  const preferred =
    documents.find((doc) => {
      if (!doc || typeof doc !== 'object') return false
      const docType = String((doc as Record<string, unknown>).docType ?? '').toLowerCase()
      return docType.includes('driver') || docType.includes('license') || docType.includes('licence')
    }) ?? documents[0]
  if (!preferred || typeof preferred !== 'object') return null
  const fields = (preferred as Record<string, unknown>).fields
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return null
  return fields as Record<string, unknown>
}

function readFieldByNames(fields: Record<string, unknown>, names: string[]): string {
  for (const name of names) {
    if (!(name in fields)) continue
    const value = fieldAsString(fields[name])
    if (value) return value
  }
  // Case-insensitive fallback
  const lowerMap = new Map(Object.keys(fields).map((k) => [k.toLowerCase(), k]))
  for (const name of names) {
    const actual = lowerMap.get(name.toLowerCase())
    if (!actual) continue
    const value = fieldAsString(fields[actual])
    if (value) return value
  }
  return ''
}

function findField(fields: Record<string, unknown>, names: string[]): unknown {
  for (const name of names) {
    if (name in fields && fields[name] != null) return fields[name]
  }
  const lowerMap = new Map(Object.keys(fields).map((k) => [k.toLowerCase(), k]))
  for (const name of names) {
    const actual = lowerMap.get(name.toLowerCase())
    if (actual && fields[actual] != null) return fields[actual]
  }
  return null
}

function normalizePostalCode(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits.length >= 9) return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`
  if (digits.length >= 5) return digits.slice(0, 5)
  return raw.trim()
}

function parseAzureAddressField(field: unknown): ParsedUsAddress {
  if (field == null) {
    return { street: '', city: '', state: '', zip: '', display: '' }
  }
  if (typeof field === 'string') return parseUsMailingAddress(field)

  if (typeof field !== 'object') return parseUsMailingAddress(String(field))
  const record = field as Record<string, unknown>
  const valueAddress =
    record.valueAddress && typeof record.valueAddress === 'object' && !Array.isArray(record.valueAddress)
      ? (record.valueAddress as Record<string, unknown>)
      : record.address && typeof record.address === 'object' && !Array.isArray(record.address)
        ? (record.address as Record<string, unknown>)
        : record

  const readPart = (...keys: string[]): string => {
    for (const key of keys) {
      const value = valueAddress[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
    return ''
  }

  const houseNumber = readPart('houseNumber', 'house_number')
  const road = readPart('road', 'street', 'streetName')
  const streetFromParts = [houseNumber, road].filter(Boolean).join(' ')
  const street = readPart('streetAddress', 'street_address', 'address1', 'line1') || streetFromParts
  const city = readPart('city')
  const state = readPart('state', 'stateProvince', 'region').toUpperCase()
  const zip = normalizePostalCode(readPart('postalCode', 'postal_code', 'zip'))
  const fallback = fieldAsString(field)
  const fromParts = formatUsAddressDisplay({ street, city, state, zip })
  if (fromParts) {
    return { street, city, state, zip, display: fromParts }
  }
  return parseUsMailingAddress(fallback)
}

function readAddressFromFields(fields: Record<string, unknown>): ParsedUsAddress {
  const addressField = findField(fields, [
    'Address',
    'address',
    'ResidentialAddress',
    'MailingAddress',
    'HomeAddress',
  ])
  const parsed = parseAzureAddressField(addressField)
  const region = readFieldByNames(fields, ['Region', 'State', 'state'])
  const postal = readFieldByNames(fields, ['PostalCode', 'Zip', 'ZipCode', 'postalCode'])
  const city = parsed.city
  const state = parsed.state || (region.length === 2 ? region.toUpperCase() : region)
  const zip = parsed.zip || normalizePostalCode(postal)
  const street = parsed.street
  const display =
    formatUsAddressDisplay({ street, city, state, zip }) || parsed.display
  if (!display) {
    return { street: '', city: '', state: '', zip: '', display: '' }
  }
  return { street, city, state, zip, display }
}

/**
 * Extract DOB, gender, mobile, and address (when available) from a driving-licence analyze result.
 * Missing fields are returned as empty strings so the UI can collect them manually.
 */
export function mapAzureDrivingLicenceResultToIntakeForm(
  payload: unknown
): IntakeFormPatchFromDrivingLicence {
  const fields = getDocumentFields(payload)
  if (!fields) return { ...EMPTY }

  const dobRaw = readFieldByNames(fields, [
    'DateOfBirth',
    'DOB',
    'BirthDate',
    'dateOfBirth',
    'Date of Birth',
  ])
  const sexRaw = readFieldByNames(fields, ['Sex', 'Gender', 'sex', 'gender'])
  const phoneRaw = readFieldByNames(fields, [
    'PhoneNumber',
    'MobileNumber',
    'Mobile',
    'TelephoneNumber',
    'Phone',
  ])
  const firstName = readFieldByNames(fields, ['FirstName', 'GivenName', 'firstName'])
  const lastName = readFieldByNames(fields, ['LastName', 'Surname', 'FamilyName', 'lastName'])
  const address = readAddressFromFields(fields)

  return {
    patientDOB: normalizeDateForInput(dobRaw),
    patientGender: normalizeGender(sexRaw),
    patientPhone: normalizePhone(phoneRaw),
    patientFirstName: firstName,
    patientLastName: lastName,
    patientAddress: address.display,
    patientCity: address.city,
    patientState: address.state,
    patientZip: address.zip,
  }
}

export function hasAnyDrivingLicenceIdentityFields(
  extracted: IntakeFormPatchFromDrivingLicence
): boolean {
  return Boolean(
    extracted.patientDOB ||
      extracted.patientGender ||
      extracted.patientPhone ||
      extracted.patientFirstName ||
      extracted.patientLastName ||
      extracted.patientAddress
  )
}
